import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  PanelFrame,
  Sidebar,
  Topbar,
  type SidebarItem,
} from '@shared/design-system';
import { Bell, LogOut, User as UserIcon, Settings as SettingsIcon } from 'lucide-react';
import {
  useAuth,
  useTenant,
  PageHeaderProvider,
  usePageHeaderActionsSlot,
} from '@/contexts';
import { Toaster } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { filterNavItems } from './navFilter';

const pageHeaders: Record<string, { title: string; subtitle?: string }> = {
  '/':                   { title: 'Gösterge Paneli',     subtitle: 'Genel bakış ve istatistikler' },
  '/tickets':            { title: 'Talepler',            subtitle: 'Destek taleplerini yönetin' },
  '/customers':          { title: 'Müşteriler',          subtitle: 'Müşteri kayıtlarını yönetin' },
  '/teams':              { title: 'Ekipler',             subtitle: 'Takım yapısını düzenleyin' },
  '/users':              { title: 'Kullanıcılar',        subtitle: 'Kullanıcı hesaplarını yönetin' },
  '/roles':              { title: 'Roller',              subtitle: 'Yetki ve rolleri düzenleyin' },
  '/settings':           { title: 'Ayarlar',             subtitle: 'Sistem ayarlarını yapılandırın' },
  '/notifications':      { title: 'Bildirimler',         subtitle: 'Bildirimlerinizi görüntüleyin' },
  '/reports':            { title: 'Raporlar',            subtitle: 'Detaylı raporlar ve analizler' },
  '/sla':                { title: 'SLA Yönetimi',        subtitle: 'Servis seviye anlaşmalarını yönetin' },
  '/audit-logs':         { title: 'Aktivite Kayıtları',  subtitle: 'Sistem aktivitelerini inceleyin' },
  '/search':             { title: 'Gelişmiş Arama',      subtitle: 'Detaylı arama ve filtreleme' },
  '/projects':           { title: 'Projeler',            subtitle: 'Proje yönetimi ve takibi' },
  '/contracts':          { title: 'Sözleşmeler',         subtitle: 'Sözleşme kayıtlarını yönetin' },
  '/quotes':             { title: 'Teklifler',           subtitle: 'Teklif ve fiyatlandırma yönetimi' },
  '/appointments':       { title: 'Randevular',          subtitle: 'Randevu planlama ve takibi' },
  '/licenses':           { title: 'Lisanslar',           subtitle: 'Lisans yönetimi' },
  '/inventory':          { title: 'Envanter',            subtitle: 'Stok ve envanter takibi' },
  '/services':           { title: 'Hizmetler',           subtitle: 'Hizmet kataloğunu yönetin' },
  '/knowledge-base':     { title: 'Bilgi Bankası',       subtitle: 'Bilgi bankası makaleleri' },
  '/announcements':      { title: 'Duyurular',           subtitle: 'Duyuruları yönetin' },
  '/problems':           { title: 'Problemler',          subtitle: 'Problem yönetimi' },
  '/change-requests':    { title: 'Değişiklik Talepleri', subtitle: 'Değişiklik taleplerini yönetin' },
  '/approvals':          { title: 'Onaylar',             subtitle: 'Onay süreçlerini takip edin' },
  '/templates':          { title: 'Şablonlar',           subtitle: 'Şablon yönetimi' },
  '/custom-fields':      { title: 'Özel Alanlar',        subtitle: 'Özel alan tanımları' },
  '/assignment-rules':   { title: 'Atama Kuralları',     subtitle: 'Otomatik atama kurallarını yönetin' },
  '/escalation-rules':   { title: 'Eskalasyon Kuralları', subtitle: 'Yükseltme kurallarını yönetin' },
  '/automation-rules':   { title: 'Otomasyon Kuralları', subtitle: 'Otomasyon kurallarını yönetin' },
  '/recurring-tickets':  { title: 'Tekrarlayan Talepler', subtitle: 'Yinelenen talepleri yönetin' },
  '/business-calendars': { title: 'İş Takvimi',          subtitle: 'Çalışma takvimlerini yapılandırın' },
  '/branding':           { title: 'Marka Ayarları',      subtitle: 'Marka ve tema ayarları' },
};

function getPageHeader(pathname: string): { title: string; subtitle?: string } {
  if (pageHeaders[pathname]) return pageHeaders[pathname];
  if (pathname.startsWith('/projects/'))        return { title: 'Proje Detayı',          subtitle: 'Proje bilgileri ve görev yönetimi' };
  if (pathname.startsWith('/tickets/'))         return { title: 'Talep Detayı',          subtitle: 'Talep bilgileri' };
  if (pathname.startsWith('/customers/'))       return { title: 'Müşteri Detayı',        subtitle: 'Müşteri bilgileri' };
  if (pathname.startsWith('/problems/'))        return { title: 'Problem Detayı',        subtitle: 'Problem bilgileri' };
  if (pathname.startsWith('/change-requests/')) return { title: 'Değişiklik Detayı',     subtitle: 'Değişiklik talebi bilgileri' };
  return { title: 'Sayfa' };
}

const navItems = (navigate: (path: string) => void): SidebarItem[] => [
  { section: 'Ana Menü' },
  { id: '/',                   icon: 'home',     label: 'Gösterge Paneli', onClick: () => navigate('/') },
  { id: '/tickets',            icon: 'ticket',   label: 'Talepler',        onClick: () => navigate('/tickets') },
  { id: '/search',             icon: 'search',   label: 'Gelişmiş Arama',  onClick: () => navigate('/search') },
  { section: 'Yönetim' },
  { id: '/customers',          icon: 'building', label: 'Müşteriler',      onClick: () => navigate('/customers') },
  { id: '/contracts',          icon: 'invoice',  label: 'Sözleşmeler',     onClick: () => navigate('/contracts') },
  { id: '/quotes',             icon: 'invoice',  label: 'Teklifler',       onClick: () => navigate('/quotes') },
  { id: '/appointments',       icon: 'calendar', label: 'Randevular',      onClick: () => navigate('/appointments') },
  { id: '/licenses',           icon: 'lock',     label: 'Lisanslar',       onClick: () => navigate('/licenses') },
  { id: '/projects',           icon: 'folder',   label: 'Projeler',        onClick: () => navigate('/projects') },
  { id: '/inventory',          icon: 'folder',   label: 'Envanter',        onClick: () => navigate('/inventory') },
  { id: '/services',           icon: 'layers',   label: 'Servisler',       onClick: () => navigate('/services') },
  { id: '/knowledge-base',     icon: 'book',     label: 'Bilgi Bankası',   onClick: () => navigate('/knowledge-base') },
  { id: '/announcements',      icon: 'bell',     label: 'Duyurular',       onClick: () => navigate('/announcements') },
  { id: '/teams',              icon: 'users',    label: 'Ekipler',         onClick: () => navigate('/teams') },
  { id: '/users',              icon: 'users',    label: 'Kullanıcılar',    onClick: () => navigate('/users') },
  { section: 'ITIL' },
  { id: '/problems',           icon: 'alert',    label: 'Problem Yönetimi',     onClick: () => navigate('/problems') },
  { id: '/change-requests',    icon: 'refresh',  label: 'Değişiklik Yönetimi',  onClick: () => navigate('/change-requests') },
  { id: '/approvals',          icon: 'check',    label: 'Onay Yönetimi',        onClick: () => navigate('/approvals') },
  { section: 'İzleme' },
  { id: '/notifications',      icon: 'bell',     label: 'Bildirimler',          onClick: () => navigate('/notifications') },
  { id: '/reports',            icon: 'chart',    label: 'Raporlar',             onClick: () => navigate('/reports') },
  { id: '/sla',                icon: 'clock',    label: 'SLA Yönetimi',         onClick: () => navigate('/sla') },
  { id: '/audit-logs',         icon: 'shield',   label: 'Aktivite Kayıtları',   onClick: () => navigate('/audit-logs') },
  { section: 'Sistem' },
  { id: '/templates',          icon: 'message',  label: 'Şablonlar',            onClick: () => navigate('/templates') },
  { id: '/custom-fields',      icon: 'settings', label: 'Özel Alanlar',         onClick: () => navigate('/custom-fields') },
  { id: '/assignment-rules',   icon: 'bolt',     label: 'Atama Kuralları',      onClick: () => navigate('/assignment-rules') },
  { id: '/escalation-rules',   icon: 'alert',    label: 'Eskalasyon Kuralları', onClick: () => navigate('/escalation-rules') },
  { id: '/automation-rules',   icon: 'bolt',     label: 'Otomasyon Kuralları',  onClick: () => navigate('/automation-rules') },
  { id: '/recurring-tickets',  icon: 'repeat',   label: 'Tekrarlayan Talepler', onClick: () => navigate('/recurring-tickets') },
  { id: '/business-calendars', icon: 'calendar', label: 'İş Takvimi',           onClick: () => navigate('/business-calendars') },
  { id: '/roles',              icon: 'shield',   label: 'Roller',               onClick: () => navigate('/roles') },
  { id: '/branding',           icon: 'star',     label: 'Marka Ayarları',       onClick: () => navigate('/branding') },
  { id: '/settings',           icon: 'settings', label: 'Ayarlar',              onClick: () => navigate('/settings') },
];

function getActiveId(items: SidebarItem[], pathname: string): string {
  const ids = items.flatMap((it) => ('id' in it && it.id ? [it.id] : []));
  const exact = ids.find((id) => id === pathname);
  if (exact) return exact;
  const prefixMatch = ids
    .filter((id) => id !== '/' && pathname.startsWith(id + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return prefixMatch ?? pathname;
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function TopbarActions() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const pageActions = usePageHeaderActionsSlot();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {pageActions}
      {pageActions && (
        <span style={{ width: 1, height: 24, background: 'var(--dg-border)', margin: '0 4px' }} />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="size-5 text-slate-600" />
            <span className="absolute right-1 top-1 flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-red-500" />
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Bildirimler</span>
            <Badge variant="default" className="text-xs">3 yeni</Badge>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-64 overflow-auto">
            <DropdownMenuItem className="flex flex-col items-start gap-1 py-3">
              <p className="text-sm font-medium">Yeni ticket atandı</p>
              <p className="text-xs text-slate-500">TKT-1234 size atandı</p>
              <p className="text-xs text-slate-400">2 dakika önce</p>
            </DropdownMenuItem>
            <DropdownMenuItem className="flex flex-col items-start gap-1 py-3">
              <p className="text-sm font-medium">SLA uyarısı</p>
              <p className="text-xs text-slate-500">TKT-1230 SLA süresi dolmak üzere</p>
              <p className="text-xs text-slate-400">15 dakika önce</p>
            </DropdownMenuItem>
            <DropdownMenuItem className="flex flex-col items-start gap-1 py-3">
              <p className="text-sm font-medium">Yorum eklendi</p>
              <p className="text-xs text-slate-500">TKT-1228'e yeni yorum eklendi</p>
              <p className="text-xs text-slate-400">1 saat önce</p>
            </DropdownMenuItem>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="justify-center text-[color:var(--dg-brand)]"
            onClick={() => navigate('/notifications')}
          >
            Tüm bildirimleri gör
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 px-2">
            <Avatar className="size-8">
              <AvatarFallback className="bg-[color:var(--dg-brand)] text-white text-sm">
                {user?.fullName ? getInitials(user.fullName) : 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium text-slate-700">{user?.fullName || 'Kullanıcı'}</p>
              <p className="text-xs text-slate-500">{user?.email || ''}</p>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div>
              <p className="font-medium">{user?.fullName}</p>
              <p className="text-xs font-normal text-slate-500">{user?.email}</p>
              {user?.roles && user.roles.length > 0 && (
                <Badge variant="secondary" className="mt-1 text-xs">
                  {user.roles[0]}
                </Badge>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <UserIcon className="mr-2 size-4" />
            Profilim
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <SettingsIcon className="mr-2 size-4" />
            Ayarlar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            <LogOut className="mr-2 size-4" />
            Çıkış Yap
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { tenant } = useTenant();
  const tenantName = tenant?.companyName ?? tenant?.name;

  const items = filterNavItems(navItems(navigate), hasPermission);
  const active = getActiveId(items, location.pathname);
  const page = getPageHeader(location.pathname);

  const footer = user
    ? {
        name: user.fullName ?? user.email,
        role: user.roles?.[0] ?? 'Operatör',
      }
    : undefined;

  return (
    <PageHeaderProvider>
      <PanelFrame
        panel="tenant"
        sidebar={
          <Sidebar
            panel="tenant"
            items={items}
            active={active}
            brandLine={tenantName}
            footer={footer}
          />
        }
        topbar={
          <Topbar
            title={page.title}
            subtitle={page.subtitle}
            search
            searchPlaceholder="Hızlı ara... (Ctrl+K)"
            hideUtilityIcons
            actions={<TopbarActions />}
          />
        }
      >
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </PanelFrame>
      <Toaster />
    </PageHeaderProvider>
  );
}
